import {
	Autocomplete,
	Box,
	Button,
	Drawer,
	IconButton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useRef } from 'react';
import { stateProvinceList } from '../../data/stateProvinceList';
import { normalizeLicensePlate } from '../../utils/normalizeLicensePlate';

interface ManualEntrySheetProps {
	open: boolean;
	onClose: () => void;
	licensePlateNumber: string;
	setLicensePlateNumber: (val: string) => void;
	selectedState: string | null;
	setSelectedState: (val: string) => void;
	onSubmit: () => void;
	canSubmit: boolean;
	helperText?: string;
}

const ManualEntrySheet: React.FC<ManualEntrySheetProps> = ({
	open,
	onClose,
	licensePlateNumber,
	setLicensePlateNumber,
	selectedState,
	setSelectedState,
	onSubmit,
	canSubmit,
	helperText,
}) => {
	const plateInputRef = useRef<HTMLInputElement | null>(null);

	// Wait for the drawer's slide-in to settle before focusing — focusing
	// mid-transition can fight the animation on iOS Safari and stop the
	// keyboard from rising.
	useEffect(() => {
		if (!open) return;
		const t = setTimeout(() => plateInputRef.current?.focus(), 250);
		return () => clearTimeout(t);
	}, [open]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!canSubmit) return;
		onSubmit();
	};

	return (
		<Drawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					borderTopLeftRadius: 16,
					borderTopRightRadius: 16,
					pb: 'env(safe-area-inset-bottom)',
				},
			}}
		>
			<Box
				component="form"
				onSubmit={handleSubmit}
				data-tour="manual-plate-sheet"
				sx={{
					p: 2,
					display: 'flex',
					flexDirection: 'column',
					gap: 1.5,
				}}
			>
				<Box
					display="flex"
					alignItems="center"
					justifyContent="space-between"
				>
					<Typography variant="subtitle1" fontWeight={500}>
						Enter license plate
					</Typography>
					<IconButton
						onClick={onClose}
						aria-label="Close"
						size="small"
					>
						<CloseIcon />
					</IconButton>
				</Box>

				<Stack direction="row" spacing={1} alignItems="flex-start">
					<TextField
						inputRef={plateInputRef}
						label="Plate"
						value={licensePlateNumber}
						onChange={(e) =>
							setLicensePlateNumber(
								normalizeLicensePlate(e.target.value),
							)
						}
						autoComplete="off"
						sx={{ flexGrow: 1 }}
						inputProps={{
							autoCapitalize: 'characters',
							autoCorrect: 'off',
							spellCheck: false,
							enterKeyHint: 'go',
						}}
					/>
					<Autocomplete
						options={stateProvinceList}
						getOptionLabel={(o) => o.code}
						renderOption={(optProps, option) => {
							const { key, ...rest } = optProps as React.HTMLAttributes<HTMLLIElement> & {
								key?: React.Key;
							};
							return (
								<li key={option.code} {...rest}>
									{option.label}
								</li>
							);
						}}
						value={
							stateProvinceList.find(
								(s) => s.code === selectedState,
							) || null
						}
						onChange={(_, newValue) =>
							setSelectedState(newValue ? newValue.code : '')
						}
						renderInput={(params) => (
							<TextField {...params} label="State" />
						)}
						sx={{ width: 110 }}
					/>
				</Stack>

				{helperText && (
					<Typography variant="caption" color="text.secondary">
						{helperText}
					</Typography>
				)}

				<Box display="flex" justifyContent="flex-end">
					<Button
						type="submit"
						variant="contained"
						size="large"
						disabled={!canSubmit}
						sx={{
							minWidth: 140,
							borderRadius: 2,
							fontWeight: 500,
						}}
					>
						Log
					</Button>
				</Box>
			</Box>
		</Drawer>
	);
};

export default ManualEntrySheet;
